import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api from '@/lib/api';
import { Package, Clock, CheckCircle2, AlertTriangle, Search } from 'lucide-react';

const statusConfig = {
  'On Track': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'At Risk': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  'Delayed': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

const buyerTabs = [
  { key: 'active', label: 'Active Orders', icon: Package },
  { key: 'completed', label: 'Completed (60 days)', icon: CheckCircle2 },
];

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('active');

  const fetchOrders = useCallback(async (lifecycle) => {
    setLoading(true);
    try {
      const res = await api.get(`/orders?lifecycle=${lifecycle}`);
      setOrders(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await api.get('/dashboard/stats');
        setStats(res.data);
      } catch (e) { console.error(e); }
    };
    loadStats();
    fetchOrders(activeTab);
  }, [fetchOrders, activeTab]);

  useEffect(() => {
    fetchOrders(activeTab);
  }, [fetchOrders, activeTab]);

  const filtered = orders.filter(o =>
    o.order_id?.toLowerCase().includes(search.toLowerCase()) ||
    o.style_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.department?.toLowerCase().includes(search.toLowerCase())
  );

  const statCards = [
    { label: 'Active Orders', value: stats.total_orders || 0, icon: Package, color: 'text-[#1C1C1C]' },
    { label: 'In Progress', value: stats.in_progress || 0, icon: Clock, color: 'text-amber-600' },
    { label: 'On Track', value: stats.on_track || 0, icon: CheckCircle2, color: 'text-emerald-600' },
    { label: 'Needs Attention', value: (stats.at_risk || 0) + (stats.delayed || 0), icon: AlertTriangle, color: 'text-red-600' },
  ];

  return (
    <DashboardLayout>
      <div data-testid="buyer-dashboard" className="space-y-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">My Orders</h1>
          <p className="text-sm text-[#52525B] mt-1">Track your production orders in real time</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((s) => (
            <div key={s.label} className="bg-white rounded-md border border-black/[0.06] p-5">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA]">{s.label}</p>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className="font-heading text-3xl font-semibold text-[#0A0A0A] mt-2">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white rounded-md border border-black/[0.06] p-1 w-fit">
          {buyerTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === tab.key ? 'bg-[#1C1C1C] text-white' : 'text-[#52525B] hover:bg-[#F5F5F5]'}`}
              data-testid={`buyer-tab-${tab.key}`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
          <Input placeholder="Search by PO, style, or department..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-10 rounded-md border-black/[0.08]" data-testid="buyer-search-input" />
        </div>

        <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-10 h-10 text-[#A1A1AA] mx-auto mb-3" />
              <p className="text-sm text-[#A1A1AA]">{activeTab === 'active' ? 'No active orders' : 'No completed orders in the last 60 days'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-black/[0.04] hover:bg-transparent">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">PO</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Style Name</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden md:table-cell">PA NO</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden lg:table-cell">Dept</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">QTY</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11 hidden md:table-cell">Delivery</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Progress</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wide text-[#A1A1AA] h-11">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((order) => {
                  const sc = statusConfig[order.status] || statusConfig['On Track'];
                  return (
                    <TableRow key={order.id} className="table-row-hover cursor-pointer border-b border-black/[0.03]" onClick={() => navigate(`/dashboard/buyer/orders/${order.id}`)} data-testid={`buyer-order-row-${order.order_id}`}>
                      <TableCell className="text-sm font-semibold text-[#0A0A0A]">{order.order_id}</TableCell>
                      <TableCell className="text-sm text-[#52525B] max-w-[180px] truncate">{order.style_name}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden md:table-cell">{order.pa_no || '-'}</TableCell>
                      <TableCell className="text-sm text-[#52525B] hidden lg:table-cell">{order.department || '-'}</TableCell>
                      <TableCell className="text-sm font-semibold text-[#0A0A0A]">{order.quantity?.toLocaleString()}</TableCell>
                      <TableCell className="text-xs text-[#52525B] hidden md:table-cell">
                        {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[80px]">
                          <Progress value={order.progress} className={`h-1.5 bg-black/[0.06] flex-1 ${order.status === 'Delayed' ? '[&>div]:bg-red-500' : order.status === 'At Risk' ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`} />
                          <span className="text-xs font-semibold text-[#0A0A0A] w-8 text-right">{order.progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={`status-badge ${sc.bg} ${sc.text}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {order.status}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
